<template>
    <div class="autosuggest absolute left-0 z-10 w-96 origin-top-right mt-2">
        <div class="bg-white rounded-lg">
            <div
                class="flex px-4 rounded-lg py-3 space-x-2 border-b items-center border-slate-200"
            >
                <div
                    class="pointer-events-none inset-y-0 flex items-center text-slate-800"
                >
                    <i class="fa-solid fa-magnifying-glass"></i>
                </div>
                <label for="property_name_search" class="sr-only">
                    Property Name Search
                </label>
                <input
                    type="text"
                    id="property_name_search"
                    class="text-sm rounded-lg text-slate-800 w-full p-0 pr-4 border-none placeholder:text-slate-500 outline-none focus:outline-none focus:ring-0 active:outline-none"
                    @keydown.enter.prevent="enter"
                    @keydown.down="down"
                    @keydown.up="up"
                    @input="change"
                    @blur="handleBlur"
                    name="query_input"
                    v-show="queryInputActive"
                    v-model="computedQuery"
                    ref="searchinput"
                    :required="requireInput"
                    autocomplete="off"
                    v-debounce="debounceTime"
                    :placeholder="placeholder"
                />
                <input
                    type="hidden"
                    :name="inputName"
                    v-model="itemValue"
                    :required="requireInput"
                />
                <ClearInputButton
                    v-show="computedQuery && computedQuery.length"
                    @clear="clearSearch"
                    class="absolute right-2"
                />
            </div>
            <ul
                ref="searchscroll"
                class="top-10 overflow-auto scroll-smooth p-2 max-h-56 shadow-lg"
                v-if="filteredProperties.length > 0"
            >
                <li
                    v-for="(item, index) in filteredProperties"
                    :key="'item-id' + index"
                    :id="item.id"
                    class="px-2 py-1.5 hover:bg-slate-100 text-sm cursor-pointer rounded mb-1 focus:outline-none focus:ring-0 active:outline-none"
                    :class="{ '!bg-slate-100': item.active }"
                    @click="chooseItem(item)"
                    tabindex="0"
                >
                    {{ item.name }}
                </li>
            </ul>
            <p
                v-if="noResultsFound"
                class="p-3 bg-white rounded-lg text-slate-800 text-sm leading-tight"
            >
                No results found, try adjusting your search
            </p>
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from 'pinia';
import { useHotelsStore } from '@/store/hotels';
import ClearInputButton from '@/components/ui/clear-input-button/clear-input-button.vue';
/**
 * <AutoSuggest :url="?" @changed="?" />
 *
 * url -
 * Example: /admin/cards/search?s=
 * It will add /api* prefix by default
 *
 * resultsArrayKey -
 * Default: 'items'
 * The key used for the array results from the api
 *
 * @changed <Event>
 * returns { name: String, value: String }
 *
 * Example result:
 *
 * "items" => [
 *     ["name" => '', "value" => ""],
 *     ["name" => '', "value" => ""],
 * ]
 *
 */
export default {
    name: 'AutoSuggest',
    props: {
        url: {
            type: String,
            required: true,
        },
        inputName: {
            type: String,
            default: 'search',
        },
        resultsArrayKey: {
            type: String,
            default: 'items',
        },
        requireInput: {
            type: Boolean,
            default: true,
        },
        requestMethod: {
            type: String,
            default: 'post', // was get, that method not allowed
        },
        placeholder: {
            type: String,
            required: false,
        },
        preloadData: {
            type: Boolean,
            default: false,
        },
        preloadedItems: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        ClearInputButton,
    },
    data: () => ({
        itemValue: '',
        itemName: '',
        items: [],
        query: '',
        queryInputActive: true,
        activeIndex: -1,
        queryStartLength: 2,
        scrollAmount: 40,
        itemChosen: false,
    }),
    watch: {
        query(val) {
            if (val.length < this.queryStartLength) {
                this.items = [];
            }
        },
        filteredProperties: {
            handler(newFilteredProperties) {
                if (newFilteredProperties.length && this.activeIndex >= 0) {
                    newFilteredProperties[this.activeIndex].active = true;
                }
            },
        },
    },
    computed: {
        ...mapState(useHotelsStore, ['autoSuggestQuery']),
        computedQuery: {
            get() {
                return this.autoSuggestQuery
                    ? this.autoSuggestQuery
                    : this.query;
            },
            set(val) {
                this.query = val;
            },
        },
        debounceTime() {
            return this.preloadData ? 100 : 600;
        },
        computedItems() {
            return this.preloadData ? this.preloadedItems : this.items;
        },
        noResultsFound() {
            return this.filteredProperties.length === 0 && !this.itemChosen;
        },
        filteredProperties() {
            return this.computedItems.filter((item) => {
                let propertyNameFilter = true;

                if (this.query && this.query.length > 1) {
                    propertyNameFilter = item.name
                        .toLowerCase()
                        .includes(this.query);
                }

                return propertyNameFilter;
            });
        },
    },
    mounted() {
        setTimeout(() => {
            this.$refs.searchinput.focus();
        }, 200);
        if (!this.preloadData) {
            this.getItems();
        }
    },
    methods: {
        ...mapActions(useHotelsStore, [
            'setPropertyName',
            'setAutosuggestQuery',
        ]),
        async getItems() {
            console.log('get items triggered')
            try {
                const resp = await ApiService.apiCall(
                    `${this.url}${this.query}`,
                    this.requestMethod
                );
                
                const resultsArray = resp.data[this.resultsArrayKey];
                if (resultsArray.length > 0) {
                    this.items = resultsArray;
                    this.filteredProperties[this.activeIndex].active = true;
                } else {
                    throw new Error(
                        `No results found for search term ${this.query}`
                    );
                }
            } catch (error) {
                console.error(error);
                this.$root.handleHttpStatus(error);
            }
        },

        change(e) {
            this.resetActiveItem();
            this.activeIndex = -1;
            if (this.preloadData) {
                //const query = e.target.value.toLowerCase();
                this.setAutosuggestQuery(this.query.toLowerCase());
                //this.setPropertyName(query);
            }

            if (!this.preloadData) {
                if (e.target.value.length >= this.queryStartLength) {
                    this.query = e.target.value;
                    this.getItems();
                }
                if (!this.query && this.noResultsFound) {
                    this.noResultsFound = false;
                }
                if (!e.target.value.length) {
                    this.getItems();
                }
            }
        },
        chooseItem(item) {
            this.itemChosen = true;
            this.query = item.name;
            this.setAutosuggestQuery(item.name);

            if (this.preloadData) {
                this.setPropertyName(item.name.toLowerCase());
                this.$emit('handleSelect', {
                    item: item,
                    route: false,
                });
            }

            if (!this.preloadData) {
                this.itemValue = item.id;
                this.itemName = item.name;
                this.queryInputActive = false;
                this.clearSearch();
                this.$emit('handleSelect', {
                    name: this.itemName,
                    value: this.itemValue,
                    slug: item.slug ? item.slug : null,
                    item: item,
                    route: true,
                });
            }
        },
        up() {
            if (this.activeIndex >= 0) {
                if (this.activeIndex > 0) {
                    this.activeIndex--;
                }
                this.resetActiveItem();
                this.filteredProperties[this.activeIndex].active = true;

                if (this.filteredProperties.length - this.activeIndex > 4) {
                    this.$refs.searchscroll.scrollTop =
                        this.$refs.searchscroll.scrollTop - this.scrollAmount;
                }
            }
        },
        down() {
            if (
                this.activeIndex > -2 &&
                this.activeIndex + 1 < this.filteredProperties.length
            ) {
                this.activeIndex++;
                this.resetActiveItem();
                this.filteredProperties[this.activeIndex].active = true;

                if (this.activeIndex > 4) {
                    this.$refs.searchscroll.scrollTop =
                        this.scrollAmount + this.$refs.searchscroll.scrollTop;
                }
            }
        },
        resetActiveItem() {
            this.filteredProperties.forEach((item) => {
                item.active = false;
            });
        },
        enter() {
            if (this.filteredProperties.length > 0 && this.activeIndex >= 0) {
                const item = this.filteredProperties[this.activeIndex];
                this.chooseItem(item);
            }
        },
        handleBlur() {
            this.resetActiveItem();
            this.$emit('blur');
        },
        // activateSearch() {
        //     this.activeIndex = -2;
        //     this.queryInputActive = true;
        //     this.itemValue = '';
        //     this.itemName = '';
        //     this.query = '';
        //     this.setPropertyName(null);
        //     setTimeout(() => {
        //         this.$refs.searchinput.focus();
        //     }, 200);
        // }, ** Not invoked anywhere - can remove?
        clearSearch() {
            this.itemChosen = false;
            this.activeIndex = -1;
            this.query = '';
            this.setAutosuggestQuery(null);
            this.items = [];
            this.resetActiveItem();
            this.setPropertyName(null);
        },
    },
};
</script>
